import PageLayout from '@/components/Layouts/PageLayout';
import { NextPageContext } from 'next';
import React, { FC, useCallback, useMemo } from 'react';
import useLayoutRenderer from '@/hooks/useLayoutRenderer';
import { MemoExoticComponent } from 'react';
import Container from '@/shared/Container';
import TrendingProducts from '@/components/Home/TrendingProducts';
import WhyBuy from '@/components/Home/WhyBuy';
import getSingleString from '@/utils/string';
import ssrErrorHandler from '@/errors/ssrErrorHandler';
import Div from '@/shared/Div';
import Accordion from '@/shared/crisp/Accordion';
import SEOContent from '@/components/Seo/Content';
import PLP from '@/components/PLP';
import { getSearchResults } from '@/actions/ecom/search';
import usePLP from '@/hooks/usePLP';
import is from '@/utils/is';
import fetchAndMergeProductsWithPrice from '@/utils/fetchAndMergeProductsWithPrice';
import Heading from '@/shared/Heading';
import Filters from '@/components/PLP/Filters';
import { CMS_COMPONENTS } from '@/constants/cms';
import { getStoreDetail } from '@/actions/ecom/store';
import NewLaunches from '@/components/Home/NewLaunches';
import SpecialEventBanner from '@/shared/crisp/StoreSpecialBanner';
import DynamicPlaceholder from '@/shared/crisp/DynamicPlaceholder';
import ProductBannerExtraLarge from '@/shared/crisp/ProductBannerExtraLarge';
import HomeProductBanners from '@/components/Home/HomeProductBanners';

const STATIC_MAP: { [key: string]: MemoExoticComponent<any> | FC<any> } = {
  [CMS_COMPONENTS.SHARED.STORE_SPECIAL_BANNER]: SpecialEventBanner,
  [CMS_COMPONENTS.SHARED.TRENDING_PRODUCTS]: TrendingProducts,
  [CMS_COMPONENTS.SHARED.WHY_BUY_FROM_US]: WhyBuy,
  [CMS_COMPONENTS.SHARED.ACCORDION]: Accordion,
  [CMS_COMPONENTS.SHARED.HTML]: SEOContent,
  [CMS_COMPONENTS.SHARED.PLP]: PLP,
  [CMS_COMPONENTS.SHARED.NEW_LAUNCHES]: NewLaunches,
  [CMS_COMPONENTS.SHARED.DYNAMIC_PLACEHOLDER]: DynamicPlaceholder,
  [CMS_COMPONENTS.PRODUCT_BANNERS.EXTRA_LARGE]: ProductBannerExtraLarge,
  [CMS_COMPONENTS.PRODUCT_BANNERS.LARGE]: HomeProductBanners,
};

const STORES_ID = 'stores.id';

export default function Category({
  mainNav,
  page,
  seo,
  name,
  plp,
  storeId,
}: {
  mainNav: any;
  page: any;
  seo: any;
  name: string;
  plp: any;
  storeId: string;
}) {
  const {
    plpList,
    open,
    isEmptyProductsList,
    closeModal,
    handleOpenModal,
    modalType,
    currentPage,
    applySortByFilter,
    viewMoreProduct,
    isLoading,
    onApplyFilter,
    onApplySortBy,
    onResetFilter,
  } = usePLP(plp, getSearchResults, null, { [STORES_ID]: [storeId] }, 12);

  const renderPLPHeader = useCallback(
    (plpProps?: any) => {
      return (
        <Div className="pb-6">
          <Container>
            <Heading level={2} className="py-s6 text-center text-h-2">
              {plpProps?.title}
            </Heading>
            {plpProps?.showFilters ? (
              <Filters
                modalType={modalType}
                closeModal={closeModal}
                open={open}
                filters={plpList.filters}
                onApplyFilter={onApplyFilter}
                onResetFilter={onResetFilter}
                onApplySortBy={onApplySortBy}
                show={!isEmptyProductsList}
                handleOpenModal={handleOpenModal}
                applySortByFilter={applySortByFilter}
              />
            ) : null}
          </Container>
        </Div>
      );
    },
    [
      modalType,
      closeModal,
      open,
      plpList.filters,
      onApplyFilter,
      onResetFilter,
      onApplySortBy,
      isEmptyProductsList,
      handleOpenModal,
      applySortByFilter,
    ],
  );

  const componentProps = useMemo(() => {
    return {
      [CMS_COMPONENTS.SHARED.PLP]: {
        isLoading,
        products: plpList.products,
        resultCount: plpList.resultCount,
        page: currentPage,
        viewMoreProduct,
        renderPLPHeader,
      },
    };
  }, [
    currentPage,
    isLoading,
    plpList.products,
    plpList.resultCount,
    renderPLPHeader,
    viewMoreProduct,
  ]);

  const layout = useLayoutRenderer(page, STATIC_MAP, componentProps);
  return (
    <PageLayout
      mainClassName="font-primary"
      hideFooterIcons
      seo={seo}
      pageWrapperClassName="font-primary "
    >
      {layout}
    </PageLayout>
  );
}

export async function getServerSideProps(nextPageContext: NextPageContext) {
  const { query } = nextPageContext;
  const storeId = getSingleString(query.storeId);
  try {
    const promises = [
      getStoreDetail(storeId),
      getSearchResults({
        q: null,
        filters: { [STORES_ID]: [storeId] },
        page: 1,
        size: 12,
      }),
    ];
    const [page, searchResults] = await Promise.all(promises);
    const { name, subCategories = [], layout, seo } = page;
    const { products, filters, pageCount = 1, resultCount = 0 } = searchResults?.data;

    const isPLPInLayout = Boolean(
      layout.find((item: any) => item.__component === CMS_COMPONENTS.SHARED.PLP),
    );

    let productsWithPrice: any = [];
    if (isPLPInLayout && is.not.empty.array(products)) {
      productsWithPrice = (await fetchAndMergeProductsWithPrice(products || [])) ?? null;
    }

    return {
      props: {
        mainNav: {
          name,
          subCategories,
        },
        page: layout,
        seo,
        plp: { products: productsWithPrice, filters, pageCount, resultCount },
        name,
        storeId,
      },
    };
  } catch (error) {
    return ssrErrorHandler(error, nextPageContext);
  }
}
